import { Box, Checkbox, Grid, Typography, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ChargeFormEntry } from 'views/orders/charges/components/GeneralChargesTable/types';
import { formatMoney } from 'utils/helpers';

const useStyles = makeStyles((theme: any) => ({
  item: {
    width: '100%',
    borderRadius: 4,
    backgroundColor: theme.palette.background.paper,
    padding: 8,
    marginTop: 8,
    cursor: 'pointer',
  },
  itemReadOnly: {
    width: '100%',
    borderRadius: 4,
    backgroundColor: theme.palette.background.paper,
    padding: 8,
    marginTop: 8,
  },
  container: {
    borderRightStyle: 'solid',
    borderRightWidth: 2,
    borderRightColor: theme.accent.main,
    maxHeight: '400px',
    minWidth: "250px",
    flex: 1,
    paddingRight: "10px"
  },
  listContainer: {
    backgroundColor: theme.palette.input.border,
    borderRadius: 8,
    padding: 16,
    maxHeight: '400px'
  },
  mobileReview: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  }
}));

interface Props {
  charges: ChargeFormEntry[];
  isMobileReview?: boolean;
  selectedIds?: Set<string>;
  onToggle?: (id: string) => void;
}

const sortCharges = (charges: ChargeFormEntry[]) =>
  [...charges].sort((a, b) =>
    (a.letter ?? '').localeCompare(b.letter ?? '') || (a.number ?? 0) - (b.number ?? 0)
  );

export const ChargesList: FC<Props> = ({
  charges,
  isMobileReview = false,
  selectedIds,
  onToggle,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const sortedCharges = sortCharges(charges);

  if (isMobileReview) {
    return (
      <Box className={classes.mobileReview}>
        {sortedCharges.map(({ accounting_id, description, amount, letter, number }) => (
          <Box key={accounting_id} className={classes.itemReadOnly}>
            <Typography variant="body2" style={{ color: theme.palette.primary.main, fontWeight: 500 }}>
              {`${letter ?? ''}-${number ?? ''} ${description}`}
            </Typography>
            <Typography variant="body2" style={{ color: theme.palette.primary.light }}>
              {formatMoney(amount)}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  const hasSelection = selectedIds !== undefined && onToggle !== undefined;

  return (
    <Box className={classes.container}>
      <Grid
        container
        direction="column"
        wrap="nowrap"
        className={classes.listContainer}
      >
        <Grid item>
          <Typography variant="body1" style={{ fontWeight: 500, color: theme.palette.primary.light }}>
            {t('charges:charges')}
          </Typography>
        </Grid>
        <Grid style={{ overflow: 'scroll', scrollbarWidth: 'thin' }}>
          {sortedCharges.map(({ accounting_id, description, letter, number }) => (
            <Grid
              item
              key={accounting_id}
              className={hasSelection ? classes.item : classes.itemReadOnly}
              onClick={hasSelection ? () => onToggle(accounting_id!) : undefined}
            >
              <Grid container direction="row" alignItems="center">
                {hasSelection && (
                  <Grid item>
                    <Checkbox
                      checked={selectedIds.has(accounting_id!)}
                      onChange={() => onToggle(accounting_id!)}
                      color="primary"
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Grid>
                )}
                <Grid item xs>
                  <Typography variant="body2" style={{ color: theme.palette.primary.main, fontWeight: 500 }}>
                    {hasSelection
                      ? `${letter ?? ''}-${number ?? ''} ${description}`
                      : description}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Box>
  );
};
